import { memo, type ReactNode, useEffect, useRef } from "react";
import { CircularProgress } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import cx from "classnames";
import WrapIf from "src/components/utils/WrapIf/WrapIf";

interface LoadingIndicatorProps {
  loading: boolean;
  disableBlur?: boolean;
  label?: string;
  children?: ReactNode;
  className?: string;
  /** Renders an absolutely-positioned Backdrop behind the indicator, dimming the parent content */
  withBackdrop?: boolean;
  /** When true, blurs and fades all sibling elements that follow this indicator in the DOM */
  blurFollowingSiblings?: boolean;
}

/** Stable subtree so parent re-renders do not restart the MUI spinner animation. */
const LoadingIndicatorSpinner = memo(CircularProgress);

export const LoadingIndicator = ({
  loading,
  disableBlur = true, // currently should not be used until further notice
  label,
  children,
  className,
  withBackdrop = false,
  blurFollowingSiblings = false,
}: LoadingIndicatorProps) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (ref.current) {
      if (!ref.current.parentElement) {
        console.error("Loader without parent element");
      }
    }
  }, []);

  if (!loading) {
    return null;
  }

  return (
    <WrapIf
      condition={withBackdrop}
      With={Backdrop}
      open
      sx={{
        position: "absolute",
        zIndex: 10,
        backdropFilter: "blur(2px) saturate(0.5)",
        backgroundColor: "light-dark(rgb(255 255 255 / 0.7), rgb(0 0 0 / 0.7))",
      }}
    >
      <div
        ref={ref}
        className={cx(
          "absolute z-50 inset-0 grid place-items-center content-center gap-5",
          {
            "backdrop-blur-xs": !disableBlur,
          },
          className
        )}
        data-test-id="loading-indicator"
        data-sibling-blur={blurFollowingSiblings ? "active" : undefined}
      >
        <LoadingIndicatorSpinner />
        {label ||
          (children && (
            <div className="text-sm text-text-secondary animate-pulse">
              {label || children}
            </div>
          ))}
      </div>
    </WrapIf>
  );
};
